package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.BorderStrong
import com.merkost.handpass.sharedweb.theme.RadiusCard
import com.merkost.handpass.sharedweb.theme.Shadow
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.Transparent
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.transitionSurfaceAction
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.dom.Div
import kotlinx.browser.window

private const val WebCardNestedInteractiveSelector =
    "a[href], button, input, select, textarea, [role=button], [role=link], [tabindex]"

enum class WebCardPadding { Compact, Default, Comfortable, Spacious }

private fun WebCardPadding.value() = when (this) {
    WebCardPadding.Compact -> WebSize.P12.value()
    WebCardPadding.Default -> WebSize.P16.value()
    WebCardPadding.Comfortable -> WebSize.P22.value()
    WebCardPadding.Spacious -> WebSize.P24.value()
}

val WebCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Surface.value())
            .border(WebSize.P1.value(), LineStyle.Solid, Border.value())
            .borderRadius(RadiusCard.value())
            .styleModifier { property("box-shadow", Shadow.Card) }
            .transitionSurfaceAction()
    }
}

val WebCardClickableStyle = WebCardStyle.extendedBy {
    base {
        Modifier.cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .border { color(BorderStrong.value()) }
            .styleModifier { property("box-shadow", Shadow.CardHover) }
    }
    focusVisible {
        Modifier
            .outline(WebSize.P0.value(), LineStyle.None, Transparent.value())
            .boxShadow(spreadRadius = WebSize.P3.value(), color = AccentRing.value())
    }
}

@Composable
fun WebCard(
    onClick: (() -> Unit)? = null,
    padding: WebCardPadding = WebCardPadding.Default,
    content: @Composable () -> Unit,
) {
    val style = if (onClick != null) WebCardClickableStyle else WebCardStyle
    Div(
        attrs = style.toAttrs {
            val clickAction = onClick
            if (clickAction != null) {
                onClick { clickAction() }
                onKeyDown { event ->
                    if (event.target == event.currentTarget && (event.key == "Enter" || event.key == " ")) {
                        event.preventDefault()
                        clickAction()
                    }
                }
                ref { element ->
                    val timeout = window.setTimeout({
                        if (element.querySelector(WebCardNestedInteractiveSelector) == null) {
                            element.setAttribute("role", "button")
                            element.setAttribute("tabindex", "0")
                        }
                    }, 0)
                    onDispose { window.clearTimeout(timeout) }
                }
            }
            style { property("padding", padding.value().toString()) }
        }
    ) {
        content()
    }
}
